import { Pagination } from './dock-care.model';

interface DccConfigurationRawGroup {
  name?: string | null;
  dcc_code?: string | null;
  code?: string | null;
  value?: string | null;
  rollout_percentage?: number | null;
  rolloutPercentage?: number | null;
}

interface DccConfigurationRawFeatureFlag {
  name?: string | null;
  description?: string | null;
  enabled?: boolean | null;
  groups?: (DccConfigurationRawGroup | string)[] | null;
}

export interface DccConfigurationGroup {
  name: string;
  rollout_percentage: number;
}

export interface DccConfigurationFeatureFlag {
  name: string;
  description: string;
  enabled: boolean;
  groups: DccConfigurationGroup[];
}

export interface DccConfigurationCreateFeatureGroupPayload {
  name: string;
  rolloutPercentage: number;
}

export interface DccConfigurationCreateFeaturePayload {
  name: string;
  description: string;
  enabled: boolean;
  groups: DccConfigurationCreateFeatureGroupPayload[];
}

export interface DccConfigurationFlagsResponse {
  success: boolean;
  data: DccConfigurationFeatureFlag[];
  pagination?: Pagination | null;
  errors?: unknown[];
  messages?: unknown[];
  status_code?: number;
  timestamp?: string;
  error_code?: string | null;
  error_message?: string | null;
  has_data?: boolean;
}

export interface DccConfigurationFeatureFlagResponse {
  success: boolean;
  data: DccConfigurationFeatureFlag;
  pagination?: Pagination | null;
  errors?: unknown[];
  messages?: unknown[];
  status_code?: number;
  timestamp?: string;
  error_code?: string | null;
  error_message?: string | null;
  has_data?: boolean;
}

export interface DccConfigOption {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  groups: DccConfigurationGroup[];
}

export interface DccConfigAccordion {
  key: string;
  label: string;
  options: DccConfigOption[];
}

export type DccExpandedAccordionState = Record<string, boolean>;
export type DccSelectedOptionState = Record<string, string>;

export function normalizeDccConfigurationFeatureFlags(
  features:
    | (DccConfigurationRawFeatureFlag | DccConfigurationFeatureFlag)[]
    | null
    | undefined,
): DccConfigurationFeatureFlag[] {
  return (features ?? []).map((feature) =>
    normalizeDccConfigurationFeatureFlag(feature),
  );
}

export function normalizeDccConfigurationFeatureFlag(
  feature:
    | DccConfigurationRawFeatureFlag
    | DccConfigurationFeatureFlag
    | null
    | undefined,
  fallback?: DccConfigurationFeatureFlag,
): DccConfigurationFeatureFlag {
  const fallbackGroups = fallback?.groups ?? [];
  const rawGroups = feature?.groups ?? fallbackGroups;
  const normalizedGroups = (rawGroups ?? [])
    .map((group, index) =>
      normalizeDccConfigurationGroup(group, fallbackGroups[index]),
    )
    .filter((group) => group.name.trim().length > 0);

  return {
    name: feature?.name ?? fallback?.name ?? '',
    description: feature?.description ?? fallback?.description ?? '',
    enabled: feature?.enabled ?? fallback?.enabled ?? false,
    groups: normalizedGroups,
  };
}

export function buildDccConfigurationAccordions(
  features: DccConfigurationFeatureFlag[],
): DccConfigAccordion[] {
  const accordionMap = new Map<string, DccConfigAccordion>();

  for (const feature of features) {
    const accordionKey = toViewKey(feature.description);
    const optionKey = toViewKey(`${feature.description}-${feature.name}`);
    const existingAccordion = accordionMap.get(accordionKey);

    const option: DccConfigOption = {
      key: optionKey,
      label: feature.name,
      description: feature.description,
      enabled: feature.enabled,
      groups: feature.groups ?? [],
    };

    if (existingAccordion) {
      existingAccordion.options.push(option);
      continue;
    }

    accordionMap.set(accordionKey, {
      key: accordionKey,
      label: feature.description,
      options: [option],
    });
  }

  return [...accordionMap.values()];
}

export function createExpandedAccordionsState(
  accordions: DccConfigAccordion[],
): DccExpandedAccordionState {
  return accordions.reduce<DccExpandedAccordionState>((state, accordion) => {
    state[accordion.key] = true;
    return state;
  }, {});
}

export function createSelectedDccByOptionState(
  accordions: DccConfigAccordion[],
): DccSelectedOptionState {
  return accordions.reduce<DccSelectedOptionState>((state, accordion) => {
    for (const option of accordion.options) {
      state[option.key] = '';
    }

    return state;
  }, {});
}

function toViewKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-');
}

function normalizeDccConfigurationGroup(
  group: DccConfigurationRawGroup | DccConfigurationGroup | string,
  fallback?: DccConfigurationGroup,
): DccConfigurationGroup {
  if (typeof group === 'string') {
    return {
      name: group,
      rollout_percentage: fallback?.rollout_percentage ?? 100,
    };
  }

  const rawGroup = group as DccConfigurationRawGroup;

  return {
    name:
      group.name ??
      rawGroup.dcc_code ??
      rawGroup.code ??
      rawGroup.value ??
      fallback?.name ??
      '',
    rollout_percentage:
      group.rollout_percentage ??
      rawGroup.rolloutPercentage ??
      fallback?.rollout_percentage ??
      100,
  };
}
